import { LibraryDefinition } from './sqlEditor.libraryloading';

const dbName = 'sqlEditor';
const recentLibraryDefinitionsStore = 'recentLibraryDefinitions';

type DbOperationCallback = (store: IDBObjectStore) => void;

/**
 * LibraryDefinitions are stores with a timestamp letting us know when it was inserted.
 */
type RecentLibraryRecord = {
    value: LibraryDefinition;
    timestamp: string; // ISO date/time
};

function doDbOperation(mode: IDBTransactionMode, callback: DbOperationCallback): void {
    const openRequest = window.indexedDB.open(dbName, 1);

    openRequest.onupgradeneeded = () => {
        const db = openRequest.result;
        db.createObjectStore(recentLibraryDefinitionsStore, {
            keyPath: 'value.view.library' // path in type RecentLibraryRecord
        });
    };

    openRequest.onsuccess = () => {
        const db = openRequest.result;
        const transaction = db.transaction(recentLibraryDefinitionsStore, mode);
        const objectStore = transaction.objectStore(recentLibraryDefinitionsStore);
        callback(objectStore);
        transaction.oncomplete = () => db.close();
    };
}

function getRecentLibraryRecords(): Promise<RecentLibraryRecord[]> {
    return new Promise((resolve, reject) => {
        doDbOperation('readonly', store => {
            const request = store.getAll();
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    });
}

export async function getRecentLibraries(): Promise<LibraryDefinition[]> {
    return (await getRecentLibraryRecords()).map(x => x.value);
}

export function removeRecentLibrary(libraryName: string): Promise<void> {
    return new Promise((resolve, reject) => {
        doDbOperation('readwrite', store => {
            const request = store.delete(libraryName);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    });
}

/**
 * Inserts or updated a LibraryDefinition to the recent list
 */
export function addRecentLibrary(library: LibraryDefinition): Promise<void> {
    return new Promise((resolve, reject) => {
        doDbOperation('readwrite', store => {
            const record: RecentLibraryRecord = {
                value: library,
                timestamp: new Date().toISOString()
            };
            const request = store.put(record);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    });
}

function timespanAgeInMinutes(isoDate: string): number {
    return (Date.now() - Date.parse(isoDate)) / 60000;
}

/**
 * Call this to update library definitions based on how old they are.
 * @param maxCacheAgeInMinutes How old must the stored library definition be before it should be updated (in minutes).
 * @param libraryDefinitionFactory A function to asynchronously fetch a LibraryDefinition by library name.
 */
export async function refreshRecentLibraries(maxCacheAgeInMinutes: number, libraryDefinitionFactory: (name: string) => Promise<LibraryDefinition>): Promise<void> {
    const libraryRecords = await getRecentLibraryRecords();
    libraryRecords.forEach(async record => {
        if (timespanAgeInMinutes(record.timestamp) > maxCacheAgeInMinutes) {
            const newDefinition = await libraryDefinitionFactory(record.value.view.library);
            await addRecentLibrary(newDefinition);
        }
    });
}
